import React, { useState, useEffect, useContext } from 'react';
import { Target, Rocket, Wrench, Star } from 'lucide-react';
import AppContext from '../../../context/Context';
import GamePlanTab from './tabs/GamePlanTab';
import NorthStarTab from './tabs/NorthStarTab';
import ToolsTab from './tabs/ToolsTab';
import './PositionSizingPlanner.css';
import Header from '../landing/landing-comp/Header';

const PositionSizingPlanner = () => {
    const { config } = useContext(AppContext);
    const [activeTab, setActiveTab] = useState('gameplan');
    const isDark = config?.isDark;

    const [gpParams, setGpParams] = useState(() => {
        const saved = localStorage.getItem('gpParams');
        return saved ? JSON.parse(saved) : {
            capital: 1000000,
            targetReturn: 20,
            lotSize: 65,
            marginPerLot: 120000,
            lots: 4,
            winPts: 15,
            lossPts: 10,
            winRate: 45,
            riskPct: 0.5
        };
    });

    const [nsParams, setNsParams] = useState(() => {
        const saved = localStorage.getItem('nsParams');
        return saved ? JSON.parse(saved) : {
            capital: 1000000,
            targetReturn: 20,
            totalYears: 5,
            tradingDays: new Array(100).fill(247),
            elapsedDays: new Array(100).fill(0),
            pnl: new Array(100).fill(0)
        };
    });

    useEffect(() => {
        localStorage.setItem('gpParams', JSON.stringify(gpParams));
    }, [gpParams]);

    useEffect(() => {
        localStorage.setItem('nsParams', JSON.stringify(nsParams));
    }, [nsParams]);

    const syncInputs = (source, value, field) => {
        if (source === 'gp') {
            if (field === 'capital') {
                setGpParams(prev => ({ ...prev, capital: value }));
                setNsParams(prev => ({ ...prev, capital: value }));
            } else if (field === 'targetReturn') {
                setGpParams(prev => ({ ...prev, targetReturn: value }));
                setNsParams(prev => ({ ...prev, targetReturn: value }));
            } else {
                setGpParams(prev => ({ ...prev, [field]: value }));
            }
        } else {
            if (field === 'capital') {
                setNsParams(prev => ({ ...prev, capital: value }));
                setGpParams(prev => ({ ...prev, capital: value }));
            } else if (field === 'targetReturn') {
                setNsParams(prev => ({ ...prev, targetReturn: value }));
                setGpParams(prev => ({ ...prev, targetReturn: value }));
            } else {
                setNsParams(prev => ({ ...prev, [field]: value }));
            }
        }
    };

    // Game Plan Results Calculation
    const [gpResults, setGpResults] = useState({});

    useEffect(() => {
        const { capital, targetReturn, lotSize, lots, winPts, lossPts, winRate, riskPct, marginPerLot } = gpParams;
        const annualProfit = (capital * (targetReturn || 0)) / 100;
        const valPerPt = (lotSize || 0) * (lots || 0);

        const ptsYear = annualProfit / (valPerPt || 1);
        const ptsMonth = ptsYear / 12;
        const ptsWeek = ptsYear / 50;
        const ptsDay = ptsYear / 250;

        const profitWinner = (winPts || 0) * valPerPt;
        const lossLoser = (lossPts || 0) * valPerPt;

        const wProb = (winRate || 0) / 100;
        const lProb = 1 - wProb;
        const evPts = (wProb * (winPts || 0)) - (lProb * (lossPts || 0));
        const evMoney = evPts * valPerPt;
        const rr = (winPts || 0) / (lossPts || 1);

        const riskAmt = (capital * (riskPct || 0)) / 100;
        const maxSlPts = Math.floor(riskAmt / (valPerPt || 1));
        const captureRate = (ptsDay / 150) * 100;

        const minSuggestedLots = Math.floor((capital * 0.4) / (marginPerLot || 120000));
        const maxSuggestedLots = Math.floor((capital * 0.6) / (marginPerLot || 120000));

        setGpResults({
            annualProfit, valPerPt, ptsYear, ptsMonth, ptsWeek, ptsDay,
            profitWinner, lossLoser, evPts, evMoney, rr, riskAmt, maxSlPts,
            captureRate, minSuggestedLots, maxSuggestedLots, wProb, lProb
        });
    }, [gpParams]);

    return (
        <>
        <Header />
        <div className={`planner-container poppins ${isDark ? 'dark' : ''}`}>
            <div className="planner-tabs">
                <button 
                    className={`planner-tab-btn ${activeTab === 'gameplan' ? 'active' : ''}`} 
                    onClick={() => setActiveTab('gameplan')}
                >
                    <Target size={16} /> Game Plan Tool
                </button>
                <button 
                    className={`planner-tab-btn ${activeTab === 'northstar' ? 'active' : ''}`} 
                    onClick={() => setActiveTab('northstar')}
                >
                    <Star size={16} /> North Star Guidance
                </button>
                <button 
                    className={`planner-tab-btn ${activeTab === 'tools' ? 'active' : ''}`} 
                    onClick={() => setActiveTab('tools')}
                >
                    <Wrench size={16} /> Tools & Calculators
                </button>
            </div>

            <div className="planner-tab-content">
                {activeTab === 'gameplan' && (
                    <GamePlanTab 
                        gpParams={gpParams} 
                        setGpParams={setGpParams} 
                        gpResults={gpResults} 
                        syncInputs={syncInputs} 
                    />
                )}
                {activeTab === 'northstar' && (
                    <NorthStarTab 
                        nsParams={nsParams} 
                        setNsParams={setNsParams} 
                        syncInputs={syncInputs} 
                    />
                )}
                {activeTab === 'tools' && <ToolsTab />}
            </div>
        </div>
        </>
    );
};

export default PositionSizingPlanner;
